import { notifications } from "@mantine/notifications";
import type { MRT_ColumnFiltersState } from "mantine-react-table";

export const formatStringArrayForTable = (value?: string[]): string => {
    if (Array.isArray(value)) return value.join(", ") || "N/A";
    return value || "N/A";
};

export function isInvalidDateRange(
    startDate: string | undefined,
    endDate: string | undefined,
): boolean {
    if (!startDate || !endDate) return false;

    const start = new Date(startDate);
    const end = new Date(endDate);
    return start > end;
}

export const validateDateRange = (filters: MRT_ColumnFiltersState): boolean => {
    for (const filter of filters) {
        if (Array.isArray(filter.value) && filter.value.length === 2) {
            const [startDate, endDate] = filter.value;
            if (isInvalidDateRange(startDate, endDate)) {
                notifications.show({
                    title: "Date Range Error",
                    message: "End date cannot be before start date",
                    color: "red",
                });
                return false;
            }
        }
    }
    return true;
};

/**
 * Flattens metadata hits from the API response with consistent structure
 */
// biome-ignore lint/suspicious/noExplicitAny: API response structure is dynamic
export const flattenMetadataHits = (data: unknown, _index: string): any[] => {
    // biome-ignore lint/suspicious/noExplicitAny: API response structure is dynamic
    const typedData = data as any;
    return (
        // biome-ignore lint/suspicious/noExplicitAny: API response structure is dynamic
        typedData?.pages.flatMap((page: any) =>
            // biome-ignore lint/suspicious/noExplicitAny: API response structure is dynamic
            page.hits.hits.map((doc: any) => ({
                ...doc,
                // Normalize metadata shape so downstream consumers can always rely on `fields`
                fields: doc._source?.metadata ?? doc._source ?? {},
            })),
        ) ?? []
    );
};

/**
 * Gets the total count of documents from the API response
 */
export const getTotalDBRowCount = (data: unknown): number => {
    // biome-ignore lint/suspicious/noExplicitAny: API response structure is dynamic
    const typedData = data as any;
    return typedData?.pages?.[0]?.hits?.total?.value ?? 0;
};

/**
 * Generates a row ID for the table based on document type and available fields
 */
// biome-ignore lint/suspicious/noExplicitAny: API response structure is dynamic
export const getRowId = (row: any): string => {
    if (!row.fields) return row._id;

    // For FSR documents, use report_name if available
    if ("report_name" in row.fields && row.fields.report_name) {
        return row.fields.report_name;
    }

    return row._id;
};
